/* eslint-disable react-refresh/only-export-components -- the palette context
   and its provider are one unit. */
/**
 * The stored theme is read through `useSyncExternalStore`, with the default as
 * the server snapshot: a `useState` lazy initializer runs during the hydration
 * render itself, so a returning visitor with any other stored preference would
 * get different markup on the client's first pass than the server sent — React
 * error #418. `prefersDark` below avoids the same mismatch the same way.
 *
 * The stored theme is a palette (#893); `src/engine/support/palette.ts` says
 * what one resolves to, and `PaletteContext` carries it to `useTheme`.
 */
import React, {
  createContext,
  useCallback,
  useLayoutEffect,
  useMemo,
  useSyncExternalStore,
} from "react";
import {
  DEFAULT_THEME,
  isTheme,
  paletteAttribute,
  resolvePalette,
  type Mode,
  type PaletteId,
  type Theme,
} from "@/engine/support/palette";

export type { Theme };

/** The key the theme is stored under, read before first paint by `__root.tsx`. */
export const THEME_STORAGE_KEY = "theme";

export type PaletteContextValue = {
  theme: Theme;
  palette: PaletteId;
  resolvedTheme: Mode;
  setTheme: (theme: Theme) => void;
};

export const PaletteContext = createContext<PaletteContextValue | null>(null);

const DARK_QUERY = "(prefers-color-scheme: dark)";

const subscribeToSystemTheme = (onChange: () => void) => {
  const mediaQuery = window.matchMedia(DARK_QUERY);
  mediaQuery.addEventListener("change", onChange);
  return () => mediaQuery.removeEventListener("change", onChange);
};

const systemPrefersDark = () => window.matchMedia(DARK_QUERY).matches;

const themeSubscribers = new Set<() => void>();

const subscribeToStoredTheme = (onChange: () => void) => {
  themeSubscribers.add(onChange);
  window.addEventListener("storage", onChange);
  return () => {
    themeSubscribers.delete(onChange);
    window.removeEventListener("storage", onChange);
  };
};

// A browser may give no storage at all: an Android WebView with DOM storage
// off exposes `localStorage` as null, and others throw on reaching it. The
// chosen theme then lasts for the visit, as the bootstrap script in
// __root.tsx already assumes.
let unstoredTheme: Theme = DEFAULT_THEME;

const getStoredTheme = (): Theme => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    // Storage answered: a value it holds that names nothing is the default,
    // not whatever was last chosen without storage.
    return isTheme(stored)
      ? stored
      : stored === null
        ? unstoredTheme
        : DEFAULT_THEME;
  } catch {
    return unstoredTheme;
  }
};

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // The stored preference is external state (localStorage), so React reads
  // it as such rather than copying it into useState — see the module
  // comment for why that copy caused a hydration mismatch.
  const theme = useSyncExternalStore<Theme>(
    subscribeToStoredTheme,
    getStoredTheme,
    () => DEFAULT_THEME,
  );

  // The operating system's preference is external state, so React reads it as
  // such. What "system" resolves to then follows from it without a second copy
  // in state that an effect has to keep in step.
  // The server snapshot matches the default's dark mode so SSR markup agrees with
  // the pre-paint theme bootstrap script in __root.tsx.
  const prefersDark = useSyncExternalStore(
    subscribeToSystemTheme,
    systemPrefersDark,
    () => true,
  );
  const { palette, mode } = resolvePalette(theme, prefersDark);

  const setTheme = useCallback((next: Theme) => {
    unstoredTheme = next;
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // No storage: `unstoredTheme` carries the choice instead.
    }
    // The native "storage" event only fires in other tabs, so this tab's
    // subscribers are notified directly.
    for (const subscriber of themeSubscribers) subscriber();
  }, []);

  // A layout effect, so the class lands before the browser paints and the page
  // never shows one theme and then the other.
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(mode);
    const attribute = paletteAttribute(palette);
    if (attribute) root.setAttribute("data-palette", attribute);
    else root.removeAttribute("data-palette");
  }, [mode, palette]);

  const value = useMemo<PaletteContextValue>(
    () => ({ theme, palette, resolvedTheme: mode, setTheme }),
    [theme, palette, mode, setTheme],
  );

  return (
    <PaletteContext.Provider value={value}>{children}</PaletteContext.Provider>
  );
}
